import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/dashboard/shared";
import { UserDetailPage } from "@/components/dashboard/user-detail-page";
import type { ScreenConfig } from "@/lib/dashboard-data";
import { cn } from "@/lib/utils";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type RoleKey = "all" | "user" | "buyer" | "seller" | "admin" | "superadmin";
type UserRow = {
  id: string;
  email: string;
  name: string;
  role: Exclude<RoleKey, "all">;
  status: string;
  latest_activity: string | null;
  created_at: string;
};

const GROUPS: { key: RoleKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "user", label: "User" },
  { key: "buyer", label: "Buyer" },
  { key: "seller", label: "Seller" },
  { key: "admin", label: "Admin" },
  { key: "superadmin", label: "Superadmin" },
];

const PAGE_SIZE = 25;

function initials(name: string, email: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return (email[0] ?? "U").toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function apiGet<T>(path: string): Promise<T> {
  return fetch(`${getAPIBaseURL()}${path}`, { headers: getAdminHeaders() }).then(async (response) => {
    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    return response.json() as Promise<T>;
  });
}

export function UsersPage(_props: { config: ScreenConfig }) {
  const [group, setGroup] = React.useState<RoleKey>("all");
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [limit, setLimit] = React.useState(PAGE_SIZE);
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const counts = useQuery({
    queryKey: ["users-summary"],
    queryFn: () => apiGet<{ counts: Record<string, number> }>("/api/v1/admin/users-summary"),
  });

  const users = useQuery({
    queryKey: ["users-list", group, search, limit],
    queryFn: () =>
      apiGet<{ users: UserRow[]; total: number }>(
        `/api/v1/admin/users?role=${group}&q=${encodeURIComponent(search)}&limit=${limit}`
      ),
  });

  if (selectedUserId) {
    return <UserDetailPage userId={selectedUserId} onBack={() => setSelectedUserId(null)} />;
  }

  const rows = users.data?.users ?? [];
  const total = users.data?.total ?? 0;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border/60 px-5 py-4">
        <h2 className="text-lg font-semibold text-foreground">Users</h2>
        <p className="text-sm text-muted-foreground">Click on an account to open its full operational profile.</p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid grid-cols-2 gap-2 border-b border-border/60 p-4 sm:grid-cols-3 xl:grid-cols-6">
          {GROUPS.map((item) => {
            const count = counts.data?.counts?.[item.key] ?? 0;
            return (
              <Button
                key={item.key}
                type="button"
                variant="outline"
                onClick={() => {
                  setGroup(item.key);
                  setLimit(PAGE_SIZE);
                }}
                className={cn(
                  "h-11 justify-between rounded-xl px-4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground",
                  group === item.key && "border-transparent bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
                )}
              >
                <span>{item.label}</span>
                <span
                  className={cn(
                    "grid min-w-6 place-items-center rounded-full px-1.5 text-[11px] tabular-nums",
                    group === item.key ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground",
                  )}
                >
                  {count.toLocaleString("en-US")}
                </span>
              </Button>
            );
          })}
        </div>

        <div className="border-b border-border/60 p-4">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setLimit(PAGE_SIZE);
              }}
              placeholder="Search name, email or user ID…"
              className="pl-9"
            />
          </div>
        </div>

        {users.isLoading ? (
          <div className="space-y-3 p-4">
            {[1, 2, 3, 4, 5].map((n) => (
              <Skeleton key={n} className="h-12 w-full" />
            ))}
          </div>
        ) : users.isError ? (
          <div className="p-6 text-sm text-destructive">Users could not be loaded.</div>
        ) : rows.length === 0 ? (
          <div className="h-28 grid place-items-center text-sm text-muted-foreground">No users in this group.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.14em]">User</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.14em]">E-mail</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.14em]">Role</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.14em]">Status</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-[0.14em]">Latest activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} className="cursor-pointer" onClick={() => setSelectedUserId(row.id)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
                          {initials(row.name, row.email)}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-foreground">{row.name || "—"}</div>
                          <div className="truncate font-mono text-xs text-muted-foreground">{row.id}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{row.email}</TableCell>
                    <TableCell>
                      <span className="inline-flex rounded-full border border-border/70 bg-card px-3 py-1.5 text-xs font-medium capitalize text-foreground">
                        {row.role === "superadmin" ? "Super admin" : row.role}
                      </span>
                    </TableCell>
                    <TableCell><StatusBadge value={row.status} /></TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(row.latest_activity)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {rows.length > 0 && (
          <div className="flex items-center justify-between border-t border-border/60 px-5 py-4 text-sm text-muted-foreground">
            <span>Showing {rows.length} of {total.toLocaleString("en-US")} users</span>
            {rows.length < total && (
              <Button variant="outline" size="sm" onClick={() => setLimit((n) => n + PAGE_SIZE)}>
                Load more
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
